import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  alpha,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import type {
  CreateInventoryCountPayload,
  DirectMaterial,
  InventoryCount,
  InventoryCountSuggestionsResponse,
  InventoryImportConfirmResponse,
  InventoryImportDetectedMaterialRef,
  InventoryImportPreviewResponse,
  UpdateInventoryCountPayload,
  WarehouseLocation,
  WarehouseLocationMaterialAssignment,
  WarehouseMap,
  WarehouseMapDetailResponse,
} from '../types/industrialCost';
import {
  confirmIndustrialCostInventoryImport,
  createIndustrialCostInventoryCount,
  deleteIndustrialCostInventoryCount,
  getIndustrialCostDirectMaterials,
  getIndustrialCostInventoryCountSuggestions,
  getIndustrialCostInventoryCounts,
  getIndustrialCostPreviousInventoryCounts,
  previewIndustrialCostInventoryImport,
  updateIndustrialCostInventoryCount,
  deleteIndustrialCostWarehouseLocationMaterialAssignment,
  getIndustrialCostWarehouseLocationMaterialAssignments,
  getIndustrialCostWarehouseMapDetail,
  getIndustrialCostWarehouseMaps,
  getIndustrialCostWarehouses,
  upsertIndustrialCostWarehouseLocationMaterialAssignment,
} from '../services/industrialCostService';

import {
  analyzeFileNormalization,
  commitFileNormalization,
  reviewFileNormalization,
} from '../fileNormalization/service';
import type { FileNormalizationAnalyzeResult } from '../fileNormalization/types';
import { IMPORT_PERMISSION_DENIED_TEXT, useCanManageImports } from '../utils/importPermission';

type InventoryRegisterRow = {
  local_id: string;
  id: string | null;
  material_id: string | null;
  is_external: boolean;
  external_material_name: string;
  supplier: string;
  previous_quantity: string;
  quantity: string;
  notes: string;
  material_code: string | null;
  material_name: string | null;
  material_unit_label: string | null;
  is_dirty: boolean;
  is_saving: boolean;
};

type InventoryImportMaterialResolution = {
  mode: 'catalog' | 'external';
  material_id?: string;
  external_material_name?: string;
};

type InventoryRowStatusFilter =
  | 'all'
  | 'pending'
  | 'completed'
  | 'dirty';

type InventorySortMode =
  | 'default'
  | 'pending'
  | 'alphabetical';

const INVENTORY_LOCATIONS_ENABLED = false;
const INVENTORY_IMPORTER_KEY = 'inventory_counts';

function normalizeInventoryLocationText(
  value: string | null | undefined,
) {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeInventorySuggestionKey(
  value: string | null | undefined,
) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function buildUniqueInventorySuggestions(
  values: Array<string | null | undefined>,
) {
  const seen = new Set<string>();

  return values
    .map((value) =>
      String(value ?? '')
        .trim()
        .replace(/\s+/g, ' '),
    )
    .filter((value) => {
      const key =
        normalizeInventorySuggestionKey(value);

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}

function buildInventoryRowLocationMaterialLabel(
  row: InventoryRegisterRow,
) {
  if (row.is_external) {
    return (
      row.external_material_name?.trim() ||
      'Material manual'
    );
  }

  return (
    row.material_name?.trim() ||
    row.material_code?.trim() ||
    'Material'
  );
}

function assignmentMatchesInventoryRow(
  assignment: WarehouseLocationMaterialAssignment,
  row: InventoryRegisterRow,
) {
  const assignmentSupplier =
    normalizeInventoryLocationText(assignment.supplier);

  const rowSupplier =
    normalizeInventoryLocationText(row.supplier);

  if (assignmentSupplier !== rowSupplier) {
    return false;
  }

  if (row.is_external) {
    return (
      assignment.is_external &&
      normalizeInventoryLocationText(
        assignment.external_material_name ??
          assignment.material_label,
      ) ===
        normalizeInventoryLocationText(
          row.external_material_name,
        )
    );
  }

  return (
    !assignment.is_external &&
    Boolean(row.material_id) &&
    assignment.material_id === row.material_id
  );
}

function buildInventoryLocationRouteKey(
  assignments: WarehouseLocationMaterialAssignment[],
) {
  if (assignments.length === 0) {
    return 'ZZZZZZZZZZ';
  }

  const sortedAssignments = [...assignments].sort(
    (left, right) => {
      const leftKey = [
        left.warehouse_map_id,
        String(left.grid_y ?? 9999).padStart(4, '0'),
        String(left.grid_x ?? 9999).padStart(4, '0'),
        String(left.level_index ?? 9999).padStart(4, '0'),
      ].join(':');

      const rightKey = [
        right.warehouse_map_id,
        String(right.grid_y ?? 9999).padStart(4, '0'),
        String(right.grid_x ?? 9999).padStart(4, '0'),
        String(right.level_index ?? 9999).padStart(4, '0'),
      ].join(':');

      return leftKey.localeCompare(rightKey);
    },
  );

  const firstAssignment = sortedAssignments[0];

  return [
    firstAssignment.warehouse_map_id,
    String(firstAssignment.grid_y ?? 9999).padStart(4, '0'),
    String(firstAssignment.grid_x ?? 9999).padStart(4, '0'),
    String(firstAssignment.level_index ?? 9999).padStart(4, '0'),
  ].join(':');
}

function buildInventoryRowAlphabeticalKey(
  row: InventoryRegisterRow,
) {
  const materialLabel = row.is_external
    ? row.external_material_name
    : `${row.material_code ?? ''} ${row.material_name ?? ''}`;

  return `${materialLabel} ${row.supplier ?? ''}`
    .trim()
    .toLocaleLowerCase('es');
}

function normalizeInventorySearchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildInventoryRowSearchText(
  row: InventoryRegisterRow,
  catalogMaterialSearchText = '',
) {
  return normalizeInventorySearchText(
    [
      row.id,
      row.local_id,
      row.material_id,
      row.material_code,
      row.material_name,
      row.material_unit_label,
      row.external_material_name,
      row.supplier,
      row.previous_quantity,
      row.quantity,
      row.notes,
      catalogMaterialSearchText,
    ]
      .filter((value) => String(value ?? '').trim() !== '')
      .join(' '),
  );
}

function getInventoryRowPendingSortWeight(
  row: InventoryRegisterRow,
) {
  const hasCurrentQuantity =
    String(row.quantity ?? '').trim() !== '';

  if (!hasCurrentQuantity) return 0;
  if (row.is_dirty) return 1;
  return 2;
}

function getInventoryLocationPickerCellSx(
  cellType: string,
) {
  switch (cellType) {
    case 'aisle':
      return {
        bgcolor: 'rgba(34,197,94,0.58)',
        borderColor: 'rgba(134,239,172,0.72)',
      };

    case 'rack':
      return {
        bgcolor: 'rgba(239,68,68,0.72)',
        borderColor: 'rgba(254,202,202,0.84)',
      };

    case 'floor_storage':
      return {
        bgcolor: 'rgba(245,158,11,0.68)',
        borderColor: 'rgba(253,230,138,0.84)',
      };

    case 'blocked':
      return {
        bgcolor: 'rgba(71,85,105,0.84)',
        borderColor: 'rgba(148,163,184,0.66)',
      };

    default:
      return {
        bgcolor: 'rgba(255,255,255,0.025)',
        borderColor: 'rgba(148,163,184,0.12)',
      };
  }
}

const MONTH_OPTIONS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const YEAR_OPTIONS = [2024, 2025, 2026, 2027, 2028];

function buildCountDate(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

function formatInventoryUnitLabel(
  value: string | null | undefined,
  fallback = '—',
) {
  const text = String(value ?? '').trim();

  if (
    !text ||
    ['nan', 'none', 'null', 'undefined'].includes(text.toLowerCase())
  ) {
    return fallback;
  }

  return text;
}

function createEmptyCatalogRow(): InventoryRegisterRow {
  return {
    local_id: crypto.randomUUID(),
    id: null,
    material_id: null,
    is_external: false,
    external_material_name: '',
    supplier: '',
    previous_quantity: '',
    quantity: '',
    notes: '',
    material_code: null,
    material_name: null,
    material_unit_label: null,
    is_dirty: true,
    is_saving: false,
  };
}

function createEmptyExternalRow(): InventoryRegisterRow {
  return {
    local_id: crypto.randomUUID(),
    id: null,
    material_id: null,
    is_external: true,
    external_material_name: '',
    supplier: '',
    previous_quantity: '',
    quantity: '',
    notes: '',
    material_code: null,
    material_name: null,
    material_unit_label: null,
    is_dirty: true,
    is_saving: false,
  };
}

function mapInventoryCountToRow(item: InventoryCount): InventoryRegisterRow {
  return {
    local_id: item.id,
    id: item.id,
    material_id: item.material_id,
    is_external: item.is_external,
    external_material_name: item.external_material_name ?? '',
    supplier: item.supplier ?? '',
    previous_quantity: '',
    quantity: String(item.quantity ?? ''),
    notes: item.notes ?? '',
    material_code: item.material_code ?? null,
    material_name: item.material_name ?? null,
    material_unit_label: item.material_unit_label ?? null,
    is_dirty: false,
    is_saving: false,
  };
}

function buildReferenceKey(row: {
  material_id: string | null;
  is_external: boolean;
  external_material_name: string | null;
  supplier: string | null;
  material_code?: string | null;
}) {
  const supplier = (row.supplier ?? '').trim().toLowerCase();

  const materialReferenceSource = row.is_external
    ? row.external_material_name
    : row.material_code ?? row.material_id;

  const materialReference = (materialReferenceSource ?? '')
    .trim()
    .toLowerCase();

  return `material::${materialReference}::${supplier}`;
}

function escapeCsvValue(value: unknown): string {
  const text = String(value ?? '');
  if (text.includes('"') || text.includes(';') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function downloadCsvFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getInventoryRowSx(row: {
  id: string | null;
  is_external: boolean;
  is_dirty: boolean;
  previous_quantity: string;
  quantity: string;
}) {
  const hasPrevious = String(row.previous_quantity ?? '').trim() !== '';
  const hasCurrent = String(row.quantity ?? '').trim() !== '';
  const isInheritedOnly = !row.id && hasPrevious && !hasCurrent;
  const isModifiedPendingSave = row.is_dirty;
  const isPersistedCurrent = !!row.id;

  let rowBackground = 'transparent';
  let leftBorder = 'transparent';

  if (isInheritedOnly) {
    rowBackground = 'rgba(59,130,246,0.035)';
    leftBorder = 'rgba(96,165,250,0.18)';
  }

  if (isPersistedCurrent) {
    rowBackground = 'rgba(16,185,129,0.03)';
    leftBorder = 'rgba(52,211,153,0.16)';
  }

  if (row.is_external) {
    rowBackground = 'rgba(168,85,247,0.04)';
    leftBorder = 'rgba(196,181,253,0.18)';
  }

  if (isModifiedPendingSave) {
    rowBackground = 'rgba(245,158,11,0.05)';
    leftBorder = 'rgba(251,191,36,0.24)';
  }

  return {
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${alpha('#94a3b8', 0.08)}`,
      bgcolor: rowBackground,
      verticalAlign: 'top',
    },
    '& .MuiTableCell-root:first-of-type': {
      borderLeft: `2px solid ${leftBorder}`,
    },
  };
}

type InventorySearchFieldProps = {
  value: string;
  onChange: (value: string) => void;
};

const InventorySearchField = memo(function InventorySearchField({
  value,
  onChange,
}: InventorySearchFieldProps) {
  const [draftValue, setDraftValue] = useState(value);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      onChange(draftValue);
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [draftValue, onChange]);

  return (
    <TextField
      label="Buscar artículo, código, proveedor, unidad o notas"
      value={draftValue}
      onChange={(event) => setDraftValue(event.target.value)}
      size="small"
      fullWidth
      sx={fieldSx}
    />
  );
});

type InventoryTabletCardProps = {
  row: InventoryRegisterRow;
  locationLabels: string[];
  canAssignLocations: boolean;
  onOpenLocations: (
    row: InventoryRegisterRow,
  ) => void;
  materialOptions: Array<{
    id: string;
    label: string;
  }>;
  manualMaterialOptions: string[];
  supplierOptions: string[];
  isBulkSaving: boolean;
  onUpdateRow: (
    localId: string,
    updater: (current: InventoryRegisterRow) => InventoryRegisterRow,
  ) => void;
  onMaterialChange: (localId: string, materialId: string) => void;
  onSave: (row: InventoryRegisterRow) => void;
  onRemove: (row: InventoryRegisterRow) => void;
};

const InventoryTabletCard = memo(function InventoryTabletCard({
  row,
  locationLabels,
  canAssignLocations,
  onOpenLocations,
  materialOptions,
  manualMaterialOptions,
  supplierOptions,
  isBulkSaving,
  onUpdateRow,
  onMaterialChange,
  onSave,
  onRemove,
}: InventoryTabletCardProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [quantityDraft, setQuantityDraft] = useState(row.quantity);

  useEffect(() => {
    setQuantityDraft(row.quantity);
  }, [row.local_id, row.quantity]);

  const hasQuantityDraftChange = quantityDraft !== row.quantity;
  const effectiveIsDirty = row.is_dirty || hasQuantityDraftChange;
  const hasCurrentQuantity = String(quantityDraft ?? '').trim() !== '';

  const commitQuantityDraft = () => {
    if (!hasQuantityDraftChange) return;

    onUpdateRow(row.local_id, (current) => ({
      ...current,
      quantity: quantityDraft,
    }));
  };

  const statusLabel = row.is_saving
    ? 'Guardando...'
    : effectiveIsDirty
      ? 'Sin guardar'
      : hasCurrentQuantity
        ? 'Completado'
        : 'Pendiente';

  const statusSx = row.is_saving
    ? {
        color: '#bae6fd',
        bgcolor: 'rgba(14,165,233,0.10)',
        borderColor: 'rgba(56,189,248,0.28)',
      }
    : effectiveIsDirty
      ? {
          color: '#fde68a',
          bgcolor: 'rgba(245,158,11,0.10)',
          borderColor: 'rgba(251,191,36,0.28)',
        }
      : hasCurrentQuantity
        ? {
            color: '#bbf7d0',
            bgcolor: 'rgba(34,197,94,0.10)',
            borderColor: 'rgba(74,222,128,0.24)',
          }
        : {
            color: '#cbd5e1',
            bgcolor: 'rgba(148,163,184,0.06)',
            borderColor: 'rgba(148,163,184,0.18)',
          };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: 3,
        bgcolor: effectiveIsDirty
          ? 'rgba(120,53,15,0.10)'
          : row.is_external
            ? 'rgba(88,28,135,0.10)'
            : 'rgba(15,23,42,0.78)',
        border: `1px solid ${
          effectiveIsDirty
            ? 'rgba(251,191,36,0.22)'
            : row.is_external
              ? 'rgba(196,181,253,0.20)'
              : 'rgba(148,163,184,0.14)'
        }`,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {row.is_external ? (
              <Autocomplete
                freeSolo
                options={manualMaterialOptions}
                value={row.external_material_name}
                onChange={(_, nextValue) =>
                  onUpdateRow(row.local_id, (current) => ({
                    ...current,
                    external_material_name:
                      nextValue ?? '',
                  }))
                }
                onInputChange={(_, nextValue) =>
                  onUpdateRow(row.local_id, (current) => ({
                    ...current,
                    external_material_name:
                      nextValue,
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Material"
                    sx={fieldSx}
                  />
                )}
              />
            ) : row.material_id ? (
              <Stack spacing={0.25}>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '1rem',
                    lineHeight: 1.25,
                  }}
                >
                  {row.material_name ?? 'Material sin nombre'}
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                  }}
                >
                  {row.material_code ?? 'Sin código'}
                </Typography>
              </Stack>
            ) : (
              <TextField
                select
                value={row.material_id ?? ''}
                onChange={(event) =>
                  onMaterialChange(row.local_id, event.target.value)
                }
                variant="outlined"
                size="small"
                fullWidth
                sx={fieldSx}
              >
                <MenuItem value="">Seleccionar material</MenuItem>
                {materialOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              px: 1,
              py: 0.45,
              borderRadius: 999,
              border: '1px solid',
              fontSize: '0.72rem',
              fontWeight: 900,
              lineHeight: 1,
              ...statusSx,
            }}
          >
            {statusLabel}
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{
              minWidth: 0,
              color: row.supplier.trim() ? '#cbd5e1' : '#64748b',
              fontSize: '0.82rem',
              fontWeight: 800,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.supplier.trim() || 'Proveedor sin informar'}
          </Typography>

          <Typography
            sx={{
              flexShrink: 0,
              color: '#94a3b8',
              fontSize: '0.78rem',
              fontWeight: 900,
            }}
          >
            {formatInventoryUnitLabel(row.material_unit_label, 'Unidad sin definir')}
          </Typography>
        </Stack>

        {row.is_external ? (
          <Autocomplete
            freeSolo
            options={supplierOptions}
            value={row.supplier}
            onChange={(_, nextValue) =>
              onUpdateRow(row.local_id, (current) => ({
                ...current,
                supplier: nextValue ?? '',
              }))
            }
            onInputChange={(_, nextValue) =>
              onUpdateRow(row.local_id, (current) => ({
                ...current,
                supplier: nextValue,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Proveedor"
                placeholder="Escribe o selecciona proveedor"
                variant="outlined"
                size="small"
                fullWidth
                sx={fieldSx}
              />
            )}
          />
        ) : null}

        {INVENTORY_LOCATIONS_ENABLED ? (
          <Stack spacing={0.6}>
            <Typography
              sx={{
                color:
                  locationLabels.length > 0
                    ? '#bae6fd'
                    : '#64748b',
                fontSize: '0.76rem',
                fontWeight: 800,
                lineHeight: 1.35,
              }}
            >
              {locationLabels.length > 0
                ? `Ubicación habitual: ${locationLabels.join(' · ')}`
                : 'Sin ubicación habitual'}
            </Typography>

            <Button
              variant="outlined"
              startIcon={<PlaceRoundedIcon />}
              onClick={() => onOpenLocations(row)}
              disabled={
                !canAssignLocations ||
                row.is_saving ||
                isBulkSaving
              }
              sx={{
                alignSelf: 'flex-start',
                minHeight: 36,
                color: '#bae6fd',
                borderColor: 'rgba(56,189,248,0.24)',
                fontWeight: 900,
                textTransform: 'none',
              }}
            >
              {locationLabels.length > 0
                ? 'Editar ubicaciones'
                : 'Asignar ubicación'}
            </Button>
          </Stack>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 0.82fr) minmax(0, 1.18fr)',
            gap: 1,
          }}
        >
          <Box
            sx={{
              p: 1.1,
              borderRadius: 2.5,
              bgcolor: 'rgba(2,6,23,0.26)',
              border: '1px solid rgba(148,163,184,0.10)',
            }}
          >
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.7rem',
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Anterior
            </Typography>

            <Typography
              sx={{
                mt: 0.3,
                color: '#cbd5e1',
                fontSize: '1.08rem',
                fontWeight: 900,
              }}
            >
              {row.previous_quantity || '—'}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.1,
              borderRadius: 2.5,
              bgcolor: 'rgba(2,6,23,0.26)',
              border: '1px solid rgba(148,163,184,0.10)',
            }}
          >
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.7rem',
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Actual
            </Typography>

            <TextField
              value={quantityDraft}
              onChange={(event) => setQuantityDraft(event.target.value)}
              onBlur={commitQuantityDraft}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="0"
              inputProps={{
                inputMode: 'decimal',
              }}
              sx={{
                ...fieldSx,
                mt: 0.5,
                '& .MuiInputBase-input': {
                  py: 0.9,
                  fontSize: '1.08rem',
                  fontWeight: 900,
                },
              }}
            />
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={() => setQuantityDraft(row.previous_quantity || '')}
            disabled={row.is_saving || isBulkSaving || !row.previous_quantity}
            sx={secondaryButtonSx}
          >
            Copiar anterior
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={() =>
              onSave({
                ...row,
                quantity: quantityDraft,
                is_dirty: effectiveIsDirty,
              })
            }
            disabled={row.is_saving || isBulkSaving}
            sx={primaryButtonSx}
          >
            {row.is_saving ? 'Guardando...' : 'Guardar'}
          </Button>

          <Button
            variant="text"
            onClick={() => setDetailsOpen((current) => !current)}
            disabled={row.is_saving || isBulkSaving}
            sx={{
              color: '#bae6fd',
              fontWeight: 800,
              textTransform: 'none',
            }}
          >
            {detailsOpen ? 'Ocultar opciones' : 'Más opciones'}
          </Button>
        </Stack>

        {detailsOpen ? (
          <Stack
            spacing={1.25}
            sx={{
              pt: 1.25,
              borderTop: '1px solid rgba(148,163,184,0.12)',
            }}
          >
            {!row.is_external ? (
              <Autocomplete
                freeSolo
                options={supplierOptions}
                value={row.supplier}
                onChange={(_, nextValue) =>
                  onUpdateRow(row.local_id, (current) => ({
                    ...current,
                    supplier: nextValue ?? '',
                  }))
                }
                onInputChange={(_, nextValue) =>
                  onUpdateRow(row.local_id, (current) => ({
                    ...current,
                    supplier: nextValue,
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Proveedor"
                    placeholder="Escribe o selecciona proveedor"
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={fieldSx}
                  />
                )}
              />
            ) : null}

            <TextField
              label="Notas o incidencia"
              value={row.notes}
              onChange={(event) =>
                onUpdateRow(row.local_id, (current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Añadir observación"
              sx={fieldSx}
            />

            <Box>
              <Button
                variant="outlined"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={() => onRemove(row)}
                disabled={row.is_saving || isBulkSaving}
                sx={{
                  ...secondaryButtonSx,
                  color: '#fda4af',
                  borderColor: 'rgba(244,63,94,0.20)',
                  '&:hover': {
                    borderColor: 'rgba(244,63,94,0.38)',
                    bgcolor: 'rgba(244,63,94,0.08)',
                  },
                }}
              >
                Eliminar línea
              </Button>
            </Box>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
});

type InventoryLocationPickerDialogProps = {
  open: boolean;

  row: InventoryRegisterRow | null;

  activeMaps: WarehouseMap[];

  rowAssignments: WarehouseLocationMaterialAssignment[];

  onClose: () => void;

  onSaved: () => Promise<void>;
};

function InventoryLocationPickerDialog({
  open,
  row,
  activeMaps,
  rowAssignments,
  onClose,
  onSaved,
}: InventoryLocationPickerDialogProps) {
  const [selectedWarehouseMapId, setSelectedWarehouseMapId] =
    useState<string>('');

  const [mapDetail, setMapDetail] =
    useState<WarehouseMapDetailResponse | null>(null);

  const [mapAssignments, setMapAssignments] =
    useState<WarehouseLocationMaterialAssignment[]>([]);

  const [selectedCellKey, setSelectedCellKey] =
    useState<string>('');

  const [selectedLocationIds, setSelectedLocationIds] =
    useState<string[]>([]);

  const [isLoading, setIsLoading] =
    useState<boolean>(false);

  const [isSaving, setIsSaving] =
    useState<boolean>(false);

  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);

  useEffect(() => {
    if (!open || !row) {
      setSelectedWarehouseMapId('');
      setMapDetail(null);
      setMapAssignments([]);
      setSelectedCellKey('');
      setSelectedLocationIds([]);
      setErrorMessage(null);
      return;
    }

    const preferredMapId =
      rowAssignments[0]?.warehouse_map_id ??
      activeMaps[0]?.id ??
      '';

    setSelectedWarehouseMapId(preferredMapId);
  }, [activeMaps, open, row, rowAssignments]);

  useEffect(() => {
    let cancelled = false;

    async function loadMap() {
      if (
        !open ||
        !row ||
        !selectedWarehouseMapId
      ) {
        setMapDetail(null);
        setMapAssignments([]);
        setSelectedCellKey('');
        setSelectedLocationIds([]);
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [detail, assignments] =
          await Promise.all([
            getIndustrialCostWarehouseMapDetail(
              selectedWarehouseMapId,
            ),
            getIndustrialCostWarehouseLocationMaterialAssignments(
              selectedWarehouseMapId,
            ),
          ]);

        if (cancelled) return;

        setMapDetail(detail);
        setMapAssignments(assignments);

        setSelectedLocationIds(
          assignments
            .filter((assignment) =>
              assignmentMatchesInventoryRow(
                assignment,
                row,
              ),
            )
            .map(
              (assignment) =>
                assignment.warehouse_location_id,
            ),
        );

        setSelectedCellKey('');
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            'No se pudo cargar el mapa y sus asignaciones.',
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadMap();

    return () => {
      cancelled = true;
    };
  }, [open, row, selectedWarehouseMapId]);

  const assignmentByLocationId = useMemo(
    () =>
      Object.fromEntries(
        mapAssignments.map((assignment) => [
          assignment.warehouse_location_id,
          assignment,
        ]),
      ),
    [mapAssignments],
  );

  const selectedCellLocations = useMemo(() => {
    if (!mapDetail || !selectedCellKey) {
      return [];
    }

    const [gridXText, gridYText] =
      selectedCellKey.split(':');

    const gridX = Number(gridXText);
    const gridY = Number(gridYText);

    return mapDetail.locations.filter(
      (location) =>
        location.grid_x === gridX &&
        location.grid_y === gridY,
    );
  }, [mapDetail, selectedCellKey]);

  function toggleLocation(
    location: WarehouseLocation,
  ) {
    if (!row) return;

    const existingAssignment =
      assignmentByLocationId[location.id];

    const assignedToCurrentRow =
      existingAssignment
        ? assignmentMatchesInventoryRow(
            existingAssignment,
            row,
          )
        : false;

    if (
      existingAssignment &&
      !assignedToCurrentRow
    ) {
      return;
    }

    setSelectedLocationIds((current) =>
      current.includes(location.id)
        ? current.filter(
            (locationId) =>
              locationId !== location.id,
          )
        : [...current, location.id],
    );
  }

  async function handleSave() {
    if (!row || !selectedWarehouseMapId) {
      return;
    }

    const materialLabel =
      buildInventoryRowLocationMaterialLabel(row);

    if (
      row.is_external &&
      !row.external_material_name?.trim()
    ) {
      setErrorMessage(
        'El material externo debe tener un nombre antes de asignar ubicaciones.',
      );
      return;
    }

    if (!row.is_external && !row.material_id) {
      setErrorMessage(
        'Selecciona el material antes de asignar ubicaciones.',
      );
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);

    try {
      const currentAssignments =
        mapAssignments.filter((assignment) =>
          assignmentMatchesInventoryRow(
            assignment,
            row,
          ),
        );

      const currentLocationIds = new Set(
        currentAssignments.map(
          (assignment) =>
            assignment.warehouse_location_id,
        ),
      );

      const nextLocationIds = new Set(
        selectedLocationIds,
      );

      const locationIdsToDelete = [
        ...currentLocationIds,
      ].filter(
        (locationId) =>
          !nextLocationIds.has(locationId),
      );

      const locationIdsToCreate = [
        ...nextLocationIds,
      ].filter(
        (locationId) =>
          !currentLocationIds.has(locationId),
      );

      await Promise.all([
        ...locationIdsToDelete.map((locationId) =>
          deleteIndustrialCostWarehouseLocationMaterialAssignment(
            locationId,
          ),
        ),

        ...locationIdsToCreate.map((locationId) =>
          upsertIndustrialCostWarehouseLocationMaterialAssignment(
            locationId,
            {
              material_id: row.is_external
                ? null
                : row.material_id,
              is_external: row.is_external,
              external_material_name:
                row.is_external
                  ? row.external_material_name?.trim() ||
                    materialLabel
                  : null,
              material_label: materialLabel,
              supplier:
                row.supplier.trim() || null,
              display_order: 0,
              is_active: true,
            },
          ),
        ),
      ]);

      await onSaved();

      onClose();
    } catch (error) {
      setErrorMessage(
        'No se pudieron guardar las ubicaciones habituales.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSaving) {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Asignar ubicaciones habituales
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {row ? (
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {buildInventoryRowLocationMaterialLabel(
                  row,
                )}
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                }}
              >
                {row.supplier.trim()
                  ? `Proveedor: ${row.supplier}`
                  : 'Sin proveedor informado'}
              </Typography>
            </Box>
          ) : null}

          {errorMessage ? (
            <Alert severity="error">
              {errorMessage}
            </Alert>
          ) : null}

          {activeMaps.length === 0 ? (
            <Alert severity="info">
              No existe ningún plano activo. El
              recuento puede continuar normalmente sin
              ubicaciones.
            </Alert>
          ) : (
            <TextField
              select
              label="Plano activo"
              value={selectedWarehouseMapId}
              onChange={(event) =>
                setSelectedWarehouseMapId(
                  event.target.value,
                )
              }
              size="small"
              fullWidth
            >
              {activeMaps.map((warehouseMap) => (
                <MenuItem
                  key={warehouseMap.id}
                  value={warehouseMap.id}
                >
                  {warehouseMap.code} ·{' '}
                  {warehouseMap.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isLoading ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ py: 2 }}
            >
              <CircularProgress size={20} />

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.84rem',
                }}
              >
                Cargando plano...
              </Typography>
            </Stack>
          ) : null}

          {!isLoading && mapDetail ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(0, 1fr) 280px',
                },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  overflow: 'auto',
                  borderRadius: 2.5,
                  bgcolor: 'rgba(2,6,23,0.38)',
                  border:
                    '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${mapDetail.warehouse_map.grid_width}, 30px)`,
                    gridAutoRows: '30px',
                    gap: '3px',
                    width: 'max-content',
                    minWidth: '100%',
                    p: 1.25,
                    justifyContent: 'center',
                  }}
                >
                  {Array.from(
                    {
                      length:
                        mapDetail.warehouse_map
                          .grid_height,
                    },
                    (_, gridY) =>
                      Array.from(
                        {
                          length:
                            mapDetail.warehouse_map
                              .grid_width,
                        },
                        (_, gridX) => ({
                          gridX,
                          gridY,
                        }),
                      ),
                  )
                    .flat()
                    .map(({ gridX, gridY }) => {
                      const cell =
                        mapDetail.cells.find(
                          (candidate) =>
                            candidate.grid_x ===
                              gridX &&
                            candidate.grid_y ===
                              gridY,
                        ) ?? null;

                      const locations =
                        mapDetail.locations.filter(
                          (location) =>
                            location.grid_x ===
                              gridX &&
                            location.grid_y ===
                              gridY,
                        );

                      const selectable =
                        locations.length > 0;

                      const cellKey =
                        `${gridX}:${gridY}`;

                      const selected =
                        selectedCellKey === cellKey;

                      return (
                        <Box
                          key={cellKey}
                          onClick={() => {
                            if (selectable) {
                              setSelectedCellKey(
                                cellKey,
                              );
                            }
                          }}
                          sx={{
                            ...(cell
                              ? getInventoryLocationPickerCellSx(
                                  cell.cell_type,
                                )
                              : getInventoryLocationPickerCellSx(
                                  'empty',
                                )),
                            width: 30,
                            height: 30,
                            borderRadius: 0.8,
                            border: '1px solid',
                            cursor: selectable
                              ? 'pointer'
                              : 'default',
                            outline: selected
                              ? '2px solid #38bdf8'
                              : 'none',
                            outlineOffset: selected
                              ? 1
                              : 0,
                            opacity:
                              cell && selectable
                                ? 1
                                : 0.62,
                          }}
                        />
                      );
                    })}
                </Box>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: 'rgba(2,6,23,0.24)',
                  border:
                    '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Huecos de la celda
                </Typography>

                {selectedCellLocations.length ===
                0 ? (
                  <Typography
                    sx={{
                      mt: 1,
                      color: '#64748b',
                      fontSize: '0.82rem',
                    }}
                  >
                    Pulsa una estantería o una zona de
                    suelo para ver sus huecos.
                  </Typography>
                ) : (
                  <Stack spacing={0.8} sx={{ mt: 1 }}>
                    {selectedCellLocations.map(
                      (location) => {
                        const assignment =
                          assignmentByLocationId[
                            location.id
                          ];

                        const assignedToCurrentRow =
                          assignment && row
                            ? assignmentMatchesInventoryRow(
                                assignment,
                                row,
                              )
                            : false;

                        const occupiedByOther =
                          Boolean(assignment) &&
                          !assignedToCurrentRow;

                        const checked =
                          selectedLocationIds.includes(
                            location.id,
                          );

                        return (
                          <Button
                            key={location.id}
                            variant={
                              checked
                                ? 'contained'
                                : 'outlined'
                            }
                            onClick={() =>
                              toggleLocation(location)
                            }
                            disabled={
                              occupiedByOther ||
                              isSaving
                            }
                            sx={{
                              justifyContent:
                                'flex-start',
                              textAlign: 'left',
                              textTransform: 'none',
                              py: 0.8,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: '0.76rem',
                                  fontWeight: 900,
                                }}
                              >
                                {
                                  location.operational_label
                                }
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.2,
                                  fontSize: '0.68rem',
                                  opacity: 0.78,
                                }}
                              >
                                {occupiedByOther
                                  ? `Asignada a: ${assignment?.material_label}`
                                  : checked
                                    ? 'Seleccionada'
                                    : 'Disponible'}
                              </Typography>
                            </Box>
                          </Button>
                        );
                      },
                    )}
                  </Stack>
                )}
              </Paper>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isSaving}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          startIcon={<PlaceRoundedIcon />}
          onClick={() => void handleSave()}
          disabled={
            !row ||
            !selectedWarehouseMapId ||
            isLoading ||
            isSaving
          }
        >
          {isSaving
            ? 'Guardando...'
            : 'Guardar ubicaciones'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function IndustrialCostInventoryRegisterView() {
  const today = new Date();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const shouldUseTabletLayout = useMediaQuery(
    '(max-width:1199.95px), (max-width:1535.95px) and (any-pointer:coarse)',
  );

  const [selectedMonth, setSelectedMonth] = useState<number>(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(today.getFullYear());
  const [materials, setMaterials] = useState<DirectMaterial[]>([]);
  const [inventorySuggestions, setInventorySuggestions] =
    useState<InventoryCountSuggestionsResponse>({
      manual_materials: [],
      suppliers: [],
      manual_material_supplier_pairs: [],
    });
  const [rows, setRows] = useState<InventoryRegisterRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState<number>(0);
  const [isBulkSaving, setIsBulkSaving] = useState<boolean>(false);
  const [searchInputTerm, setSearchInputTerm] = useState<string>('');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [rowStatusFilter, setRowStatusFilter] =
    useState<InventoryRowStatusFilter>('all');

  const [sortMode, setSortMode] =
    useState<InventorySortMode>('default');

  const [mobileActionsOpen, setMobileActionsOpen] =
    useState<boolean>(false);

  const [activeWarehouseMaps, setActiveWarehouseMaps] =
    useState<WarehouseMap[]>([]);

  const [
    warehouseLocationAssignments,
    setWarehouseLocationAssignments,
  ] = useState<
    WarehouseLocationMaterialAssignment[]
  >([]);

  const [
    locationDialogRow,
    setLocationDialogRow,
  ] = useState<InventoryRegisterRow | null>(
    null,
  );

  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);
  const [importStep, setImportStep] = useState<1 | 2>(1);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<InventoryImportPreviewResponse | null>(null);
  const [importConfirmSummary, setImportConfirmSummary] =
    useState<InventoryImportConfirmResponse | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);

  const [importNormalization, setImportNormalization] =
    useState<FileNormalizationAnalyzeResult | null>(null);

  const [importSheetName, setImportSheetName] =
    useState('');

  const [importHeaderRowInput, setImportHeaderRowInput] =
    useState('1');
  const [importMapping, setImportMapping] = useState<Record<string, string | null>>({
    material: null,
    supplier: null,
    quantity: null,
    notes: null,
  });
  const [materialResolution, setMaterialResolution] = useState<
    Record<string, InventoryImportMaterialResolution>
  >({});

  const countDate = useMemo(
    () => buildCountDate(selectedYear, selectedMonth),
    [selectedYear, selectedMonth],
  );

  const activeMaterials = useMemo(
    () => materials.filter((material) => material.is_active),
    [materials],
  );

  const materialOptions = useMemo(
    () =>
      activeMaterials.map((material) => ({
        id: material.id,
        label: `${material.code} · ${material.name}`,
        code: material.code,
        name: material.name,
        unit_label: material.unit_label,
      })),
    [activeMaterials],
  );

  const manualMaterialOptions = useMemo(
    () =>
      buildUniqueInventorySuggestions([
        ...inventorySuggestions.manual_materials.map(
          (item) => item.value,
        ),
        ...rows
          .filter((row) => row.is_external)
          .map(
            (row) =>
              row.external_material_name,
          ),
      ]),
    [inventorySuggestions.manual_materials, rows],
  );

  const materialSearchTextById = useMemo(
    () =>
      Object.fromEntries(
        materials.map((material) => [
          material.id,
          normalizeInventorySearchText(
            Object.values(material)
              .map((value) => String(value ?? ''))
              .join(' '),
          ),
        ]),
      ),
    [materials],
  );

  const rowSearchTextByLocalId = useMemo(
    () =>
      Object.fromEntries(
        rows.map((row) => [
          row.local_id,
          buildInventoryRowSearchText(
            row,
            row.material_id
              ? materialSearchTextById[row.material_id] ?? ''
              : '',
          ),
        ]),
      ),
    [materialSearchTextById, rows],
  );

  const dirtyRowsCount = useMemo(
    () => rows.filter((row) => row.is_dirty).length,
    [rows],
  );

  async function reloadWarehouseLocationMetadata() {
    if (!INVENTORY_LOCATIONS_ENABLED) {
      setActiveWarehouseMaps([]);
      setWarehouseLocationAssignments([]);
      return;
    }

    try {
      const warehouses =
        await getIndustrialCostWarehouses();

      const mapsByWarehouse =
        await Promise.all(
          warehouses.map((warehouse) =>
            getIndustrialCostWarehouseMaps(
              warehouse.id,
            ),
          ),
        );

      const activeMaps = mapsByWarehouse
        .flat()
        .filter(
          (warehouseMap) =>
            warehouseMap.is_active &&
            warehouseMap.status === 'active',
        );

      const assignmentsByMap =
        await Promise.all(
          activeMaps.map((warehouseMap) =>
            getIndustrialCostWarehouseLocationMaterialAssignments(
              warehouseMap.id,
            ),
          ),
        );

      setActiveWarehouseMaps(activeMaps);

      setWarehouseLocationAssignments(
        assignmentsByMap.flat(),
      );
    } catch (error) {
      console.error(
        'No se pudo cargar la capa opcional de ubicaciones.',
        error,
      );

      setActiveWarehouseMaps([]);
      setWarehouseLocationAssignments([]);
    }
  }

  useEffect(() => {
    void reloadWarehouseLocationMetadata();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadInventorySuggestions() {
      try {
        const response =
          await getIndustrialCostInventoryCountSuggestions();

        if (!cancelled) {
          setInventorySuggestions(response);
        }
      } catch (error) {
        console.error(
          'No se pudieron cargar las sugerencias históricas de inventario.',
          error,
        );
      }
    }

    void loadInventorySuggestions();

    return () => {
      cancelled = true;
    };
  }, []);

  const locationAssignmentsByRowLocalId =
    useMemo(
      () =>
        Object.fromEntries(
          rows.map((row) => [
            row.local_id,
            warehouseLocationAssignments
              .filter((assignment) =>
                assignmentMatchesInventoryRow(
                  assignment,
                  row,
                ),
              )
              .sort((left, right) =>
                buildInventoryLocationRouteKey([
                  left,
                ]).localeCompare(
                  buildInventoryLocationRouteKey([
                    right,
                  ]),
                ),
              ),
          ]),
        ),
      [rows, warehouseLocationAssignments],
    );

  const inventorySummary = useMemo(() => {
    const inheritedOnly = rows.filter((row) => {
      const hasPrevious = String(row.previous_quantity ?? '').trim() !== '';
      const hasCurrent = String(row.quantity ?? '').trim() !== '';
      return !row.id && hasPrevious && !hasCurrent;
    }).length;

    const completed = rows.filter(
      (row) => String(row.quantity ?? '').trim() !== '',
    ).length;

    const pending = rows.filter(
      (row) => String(row.quantity ?? '').trim() === '',
    ).length;

    const external = rows.filter((row) => row.is_external).length;

    return {
      total: rows.length,
      inheritedOnly,
      completed,
      pending,
      external,
      dirty: dirtyRowsCount,
    };
  }, [rows, dirtyRowsCount]);

  const inventoryCompletionPercentage = useMemo(() => {
    if (inventorySummary.total === 0) return 0;

    return Math.round(
      (inventorySummary.completed / inventorySummary.total) * 100,
    );
  }, [inventorySummary.completed, inventorySummary.total]);

  const providerOptions = useMemo(
    () =>
      buildUniqueInventorySuggestions([
        ...inventorySuggestions.suppliers.map(
          (item) => item.value,
        ),
        ...rows.map((row) => row.supplier),
      ]).sort((left, right) =>
        left.localeCompare(right, 'es'),
      ),
    [inventorySuggestions.suppliers, rows],
  );

  const getSupplierSuggestionsForRow = (
    row: InventoryRegisterRow,
  ) => {
    if (!row.is_external) {
      return providerOptions;
    }

    const materialKey =
      normalizeInventorySuggestionKey(
        row.external_material_name,
      );

    if (!materialKey) {
      return providerOptions;
    }

    return buildUniqueInventorySuggestions([
      ...inventorySuggestions
        .manual_material_supplier_pairs
        .filter(
          (pair) =>
            normalizeInventorySuggestionKey(
              pair.material_name,
            ) === materialKey,
        )
        .map((pair) => pair.supplier),
      ...providerOptions,
    ]);
  };

  const filteredRows = useMemo(() => {
    const normalizedSearch =
      normalizeInventorySearchText(searchInputTerm);

    const nextRows = rows.filter((row) => {
      const matchesSearch =
        normalizedSearch === '' ||
        (rowSearchTextByLocalId[row.local_id] ?? '').includes(normalizedSearch);

      const matchesSupplier =
        supplierFilter === 'all' ||
        (row.supplier ?? '').trim() ===
          supplierFilter;

      const hasCurrentQuantity =
        String(row.quantity ?? '').trim() !== '';

      const matchesStatus =
        rowStatusFilter === 'all' ||
        (rowStatusFilter === 'pending' &&
          (!hasCurrentQuantity ||
            row.is_dirty)) ||
        (rowStatusFilter === 'completed' &&
          hasCurrentQuantity) ||
        (rowStatusFilter === 'dirty' &&
          row.is_dirty);

      return (
        matchesSearch &&
        matchesSupplier &&
        matchesStatus
      );
    });

    if (sortMode === 'default') {
      return nextRows;
    }

    const rowOrderByLocalId = new Map(
      rows.map((row, index) => [row.local_id, index]),
    );

    return [...nextRows].sort((left, right) => {
      if (sortMode === 'pending') {
        const weightDifference =
          getInventoryRowPendingSortWeight(left) -
          getInventoryRowPendingSortWeight(right);

        if (weightDifference !== 0) {
          return weightDifference;
        }
      }

      if (sortMode === 'alphabetical') {
        const labelDifference =
          buildInventoryRowAlphabeticalKey(left).localeCompare(
            buildInventoryRowAlphabeticalKey(right),
            'es',
          );

        if (labelDifference !== 0) {
          return labelDifference;
        }
      }

      return (
        (rowOrderByLocalId.get(left.local_id) ?? 0) -
        (rowOrderByLocalId.get(right.local_id) ?? 0)
      );
    });
  }, [
    rows,
    searchInputTerm,
    rowSearchTextByLocalId,
    supplierFilter,
    rowStatusFilter,
    sortMode,
  ]);

  const selectedMonthLabel = useMemo(
    () => MONTH_OPTIONS.find((option) => option.value === selectedMonth)?.label ?? 'Mes',
    [selectedMonth],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadMaterials() {
      setIsLoadingMaterials(true);
      try {
        const response = await getIndustrialCostDirectMaterials();
        if (!cancelled) {
          setMaterials(response);
        }
      } catch {
        if (!cancelled) {
          setErrorMessage('No se pudieron cargar los materiales del sistema.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingMaterials(false);
        }
      }
    }

    void loadMaterials();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadCounts() {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [currentRows, previousData] = await Promise.all([
          getIndustrialCostInventoryCounts(countDate),
          getIndustrialCostPreviousInventoryCounts(countDate),
        ]);

        if (cancelled) return;

        const previousRows = previousData.rows ?? [];
        const previousByKey = new Map<string, InventoryCount>();
        previousRows.forEach((row) => {
          previousByKey.set(buildReferenceKey(row), row);
        });

        const currentByKey = new Map<string, InventoryCount>();
        currentRows.forEach((row) => {
          currentByKey.set(buildReferenceKey(row), row);
        });

        const mergedRows: InventoryRegisterRow[] = [];
        const seenKeys = new Set<string>();

        previousRows.forEach((previousRow) => {
          const key = buildReferenceKey(previousRow);
          const currentRow = currentByKey.get(key);

          if (currentRow) {
            mergedRows.push({
              ...mapInventoryCountToRow(currentRow),
              previous_quantity:
                previousRow.quantity !== null && previousRow.quantity !== undefined
                  ? String(previousRow.quantity)
                  : '',
            });
          } else {
            const mappedPrevious = mapInventoryCountToRow(previousRow);
            mergedRows.push({
              ...mappedPrevious,
              id: null,
              local_id: crypto.randomUUID(),
              quantity: '',
              previous_quantity:
                previousRow.quantity !== null && previousRow.quantity !== undefined
                  ? String(previousRow.quantity)
                  : '',
              is_dirty: false,
            });
          }

          seenKeys.add(key);
        });

        currentRows.forEach((currentRow) => {
          const key = buildReferenceKey(currentRow);
          if (seenKeys.has(key)) return;

          mergedRows.push({
            ...mapInventoryCountToRow(currentRow),
            previous_quantity: '',
          });
        });

        setRows(mergedRows);
      } catch {
        if (!cancelled) {
          setErrorMessage('No se pudieron cargar los recuentos para el mes seleccionado.');
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadCounts();

    return () => {
      cancelled = true;
    };
  }, [countDate, reloadVersion]);

  const updateRow = (
    localId: string,
    updater: (current: InventoryRegisterRow) => InventoryRegisterRow,
  ) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.local_id === localId ? { ...updater(row), is_dirty: true } : row,
      ),
    );
  };

  const handleMaterialChange = (localId: string, materialId: string) => {
    const selectedMaterial = activeMaterials.find((material) => material.id === materialId) ?? null;

    updateRow(localId, (row) => ({
      ...row,
      material_id: materialId || null,
      material_code: selectedMaterial?.code ?? null,
      material_name: selectedMaterial?.name ?? null,
      material_unit_label: selectedMaterial?.unit_label ?? null,
    }));
  };

  const addCatalogRow = () => {
    setRows((currentRows) => [...currentRows, createEmptyCatalogRow()]);
  };

  const addExternalRow = () => {
    setRows((currentRows) => [...currentRows, createEmptyExternalRow()]);
  };

  const copyPreviousQuantity = (localId: string) => {
    updateRow(localId, (row) => ({
      ...row,
      quantity: row.previous_quantity || '',
    }));
  };

  const removeRow = async (row: InventoryRegisterRow) => {
    setErrorMessage(null);

    if (!row.id) {
      setRows((currentRows) => currentRows.filter((item) => item.local_id !== row.local_id));
      return;
    }

    try {
      await deleteIndustrialCostInventoryCount(row.id);
      setRows((currentRows) => currentRows.filter((item) => item.local_id !== row.local_id));
    } catch {
      setErrorMessage('No se pudo eliminar la línea seleccionada.');
    }
  };

  const saveRow = async (
    row: InventoryRegisterRow,
    options?: { silent?: boolean },
  ): Promise<boolean> => {
    const silent = options?.silent ?? false;
    setErrorMessage(null);  

    const parsedQuantity = Number(String(row.quantity).replace(',', '.'));

        if (!Number.isFinite(parsedQuantity) || parsedQuantity < 0) {
          if (!silent) {
            setErrorMessage('La cantidad actual debe ser un número válido mayor o igual que cero.');
          }
          return false;
        }

        if (row.is_external) {
          if (!row.external_material_name.trim()) {
            if (!silent) {
              setErrorMessage('Las líneas externas requieren nombre de material.');
            }
            return false;
          }
        } else if (!row.material_id) {
          if (!silent) {
            setErrorMessage('Debes seleccionar un material del sistema.');
          }
          return false;
        }

    setRows((currentRows) =>
      currentRows.map((item) =>
        item.local_id === row.local_id ? { ...item, is_saving: true } : item,
      ),
    );

    try {
      if (row.id) {
        const payload: UpdateInventoryCountPayload = {
          material_id: row.is_external ? null : row.material_id,
          is_external: row.is_external,
          external_material_name: row.is_external ? row.external_material_name.trim() : null,
          supplier: row.supplier.trim() || null,
          quantity: parsedQuantity,
          notes: row.notes.trim() || null,
        };

        const updated = await updateIndustrialCostInventoryCount(row.id, payload);

        setRows((currentRows) =>
          currentRows.map((item) =>
            item.local_id === row.local_id
              ? {
                  ...mapInventoryCountToRow(updated),
                  previous_quantity: row.previous_quantity,
                }
              : item,
          ),
        );
      } else {
        const payload: CreateInventoryCountPayload = {
          count_date: countDate,
          material_id: row.is_external ? null : row.material_id,
          is_external: row.is_external,
          external_material_name: row.is_external ? row.external_material_name.trim() : null,
          supplier: row.supplier.trim() || null,
          quantity: parsedQuantity,
          notes: row.notes.trim() || null,
        };

        const created = await createIndustrialCostInventoryCount(payload);

        setRows((currentRows) =>
          currentRows.map((item) =>
            item.local_id === row.local_id
              ? {
                  ...mapInventoryCountToRow(created),
                  previous_quantity: row.previous_quantity,
                }
              : item,
          ),
        );
      }
    } catch {
      setRows((currentRows) =>
        currentRows.map((item) =>
          item.local_id === row.local_id ? { ...item, is_saving: false } : item,
        ),
      );
      if (!silent) {
        setErrorMessage(
          'No se pudo guardar la línea. Revisa duplicados por mes + material/proveedor o campos obligatorios.',
        );
      }
      return false;
    }

    return true;
  };

  const saveAllDirtyRows = async () => {
    const dirtyRows = rows.filter((row) => row.is_dirty);

    if (!dirtyRows.length) {
      return;
    }

    setErrorMessage(null);
    setIsBulkSaving(true);

    try {
      for (const row of dirtyRows) {
        const ok = await saveRow(row, { silent: true });
        if (!ok) {
          setErrorMessage(
            'No se pudieron guardar todos los cambios. Revisa las filas pendientes e inténtalo de nuevo.',
          );
          return;
        }
      }
    } finally {
      setIsBulkSaving(false);
    }
  };

  const exportCurrentMonth = () => {
    const headers = [
      'fecha_corte',
      'tipo_material',
      'material_codigo',
      'material_nombre',
      'material_externo',
      'proveedor',
      'recuento_anterior',
      'recuento_actual',
      'unidad',
      'notas',
    ];

    const csvRows = rows.map((row) => {
      const materialType = row.is_external ? 'external' : 'catalog';
      const currentQuantity =
        row.quantity && String(row.quantity).trim() !== '' ? row.quantity : '';

      return [
        countDate,
        materialType,
        row.material_code ?? '',
        row.material_name ?? '',
        row.is_external ? row.external_material_name ?? '' : '',
        row.supplier ?? '',
        row.previous_quantity ?? '',
        currentQuantity,
        formatInventoryUnitLabel(row.material_unit_label, ''),
        row.notes ?? '',
      ]
        .map(escapeCsvValue)
        .join(';');
    });

    const monthLabel = String(selectedMonth).padStart(2, '0');
    const filename = `inventory-counts-${selectedYear}-${monthLabel}.csv`;
    const csvContent = [headers.join(';'), ...csvRows].join('\n');

    downloadCsvFile(filename, csvContent);
  };

  // IND-007
  const canImport = useCanManageImports();

  const openImportDialog = () => {
    if (!canImport) return;
    setImportDialogOpen(true);
    setImportStep(1);
    setImportError(null);
    setImportConfirmSummary(null);
  };

  const closeImportDialog = () => {
    setImportDialogOpen(false);
    setImportStep(1);
    setImportFile(null);
    setImportPreview(null);
    setImportError(null);
    setImportConfirmSummary(null);
    setMaterialResolution({});
    setImportNormalization(null);
    setImportSheetName('');
    setImportHeaderRowInput('1');
    setImportMapping({
      material: null,
      supplier: null,
      quantity: null,
      notes: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportFileSelected = async (file: File) => {
    setImportFile(file);
    setImportPreview(null);
    setImportNormalization(null);
    setImportError(null);
    setImportConfirmSummary(null);
    setMaterialResolution({});
    setIsPreviewLoading(true);

    try {
      const normalization = await analyzeFileNormalization(
        file,
        INVENTORY_IMPORTER_KEY,
      );

      setImportNormalization(normalization);
      setImportSheetName(normalization.selected_sheet ?? '');
      setImportHeaderRowInput(
        String(normalization.header_row_index + 1),
      );

      const preview =
        await previewIndustrialCostInventoryImport(
          file,
          countDate,
          {
            sheet_name: normalization.selected_sheet,
            header_row_index:
              normalization.header_row_index,
          },
          normalization.applied_column_mapping ??
            undefined,
        );

      setImportPreview(preview);
      setImportMapping(preview.auto_mapping);
    } catch {
      setImportError(
        'No se pudo generar la previsualización del archivo.',
      );
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleApplyImportSheet = async () => {
    if (!importFile) return;

    const headerRowNumber = Number(importHeaderRowInput);

    if (
      !Number.isInteger(headerRowNumber) ||
      headerRowNumber < 1
    ) {
      setImportError(
        'La fila de cabecera debe ser un número entero mayor o igual que 1.',
      );
      return;
    }

    setIsPreviewLoading(true);
    setImportError(null);
    setMaterialResolution({});

    try {
      // Solo releer la normalizacion al cambiar de hoja; seleccionar una fila
      // en la tabla es instantaneo y no dispara solicitudes al servidor.
      let normalization = importNormalization;
      if (!normalization || normalization.selected_sheet !== (importSheetName || null)) {
        normalization = await reviewFileNormalization(
          importFile,
          INVENTORY_IMPORTER_KEY,
          headerRowNumber - 1,
          importSheetName || null,
        );
      }
      const selectedSheet = normalization.selected_sheet ?? importSheetName;
      const preview = await previewIndustrialCostInventoryImport(
        importFile,
        countDate,
        {
          sheet_name: selectedSheet || null,
          header_row_index: headerRowNumber - 1,
        },
      );

      setImportNormalization({
        ...normalization,
        header_row_index: headerRowNumber - 1,
        data_start_row_index: headerRowNumber,
      });
      setImportSheetName(selectedSheet || '');
      setImportPreview(preview);
      setImportMapping(preview.auto_mapping);
    } catch {
      setImportError('No se pudo leer la hoja y cabecera seleccionadas.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const goToImportStepTwo = async () => {
    if (!importFile || !importPreview) {
      setImportError(
        'Debes seleccionar un archivo y generar la previsualización.',
      );
      return;
    }

    if (!importMapping.material || !importMapping.quantity) {
      setImportError(
        'Debes mapear los campos obligatorios: material y cantidad.',
      );
      return;
    }

    const headerRowNumber = Number(importHeaderRowInput);

    if (
      !Number.isInteger(headerRowNumber) ||
      headerRowNumber < 1
    ) {
      setImportError(
        'La fila de cabecera debe ser un número entero mayor o igual que 1.',
      );
      return;
    }

    setIsPreviewLoading(true);
    setImportError(null);

    try {
      const preview =
        await previewIndustrialCostInventoryImport(
          importFile,
          countDate,
          {
            sheet_name: importSheetName || null,
            header_row_index: headerRowNumber - 1,
          },
          importMapping,
        );

      setImportPreview(preview);
      setImportMapping(preview.auto_mapping);
      setImportStep(2);
    } catch {
      setImportError(
        'No se pudo validar el archivo con el mapeo seleccionado.',
      );
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const markAllUnresolvedAsExternal = () => {
    if (!importPreview) return;

    setMaterialResolution((current) => {
      const next = { ...current };

      importPreview.detected_material_refs.forEach((item) => {
        if (item.is_resolved) return;
        if (next[item.normalized_value]) return;

        next[item.normalized_value] = {
          mode: 'external',
          external_material_name: item.source_value,
        };
      });

      return next;
    });
  };

  const handleChangeResolutionMode = (
    detected: InventoryImportDetectedMaterialRef,
    nextValue: string,
  ) => {
    if (!nextValue) {
      setMaterialResolution((current) => {
        const clone = { ...current };
        delete clone[detected.normalized_value];
        return clone;
      });
      return;
    }

    if (nextValue === '__external__') {
      setMaterialResolution((current) => ({
        ...current,
        [detected.normalized_value]: {
          mode: 'external',
          external_material_name: detected.source_value,
        },
      }));
      return;
    }

    setMaterialResolution((current) => ({
      ...current,
      [detected.normalized_value]: {
        mode: 'catalog',
        material_id: nextValue,
      },
    }));
  };

  const handleConfirmImport = async () => {
    if (!importFile || !importPreview) {
      setImportError('Debes seleccionar un archivo y generar la previsualización.');
      return;
    }

    if (
      !importMapping.material ||
      !importMapping.quantity
    ) {
      setImportError('Debes mapear al menos los campos obligatorios: material y quantity.');
      return;
    }

    setIsConfirmLoading(true);
    setImportError(null);

    try {
      const headerRowNumber = Number(importHeaderRowInput);

      const summary = await confirmIndustrialCostInventoryImport(
        importFile,
        countDate,
        importMapping,
        materialResolution,
        {
          sheet_name: importSheetName || null,
          header_row_index: headerRowNumber - 1,
        },
      );

      try {
        await commitFileNormalization({
          importer_key: INVENTORY_IMPORTER_KEY,
          sheet_name: importSheetName || null,
          header_row_index: headerRowNumber - 1,
          data_start_row_index: headerRowNumber,
          column_mapping: Object.fromEntries(
            Object.entries(importMapping).filter(
              (
                entry,
              ): entry is [string, string] =>
                typeof entry[1] === 'string' &&
                entry[1].trim().length > 0,
            ),
          ),
          known_columns: importPreview.columns,
        });
      } catch (profileError) {
        console.warn(
          'El recuento se importó, pero no se pudo guardar el perfil de importación.',
          profileError,
        );
      }

      setImportConfirmSummary(summary);
      setReloadVersion((current) => current + 1);

      window.setTimeout(() => {
        closeImportDialog();
      }, 500);
    } catch {
      setImportError('No se pudo confirmar la importación.');
    } finally {
      setIsConfirmLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            void handleImportFileSelected(file);
          }
        }}
      />

      <Box
        sx={{
          border: `1px solid ${alpha('#94a3b8', 0.14)}`,
          bgcolor: 'rgba(8,18,36,0.9)',
          borderRadius: 4,
          px: { xs: 2, md: 3 },
          py: { xs: 1.75, lg: 2.75 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1.5, md: 2 }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#f8fafc',
                fontSize: { xs: '1.2rem', md: '1.6rem' },
                fontWeight: 900,
                letterSpacing: '-0.03em',
              }}
            >
              Inventario · Registro
            </Typography>

            <Typography
              sx={{
                display: shouldUseTabletLayout ? 'none' : 'block',
                mt: 0.75,
                color: '#94a3b8',
                maxWidth: 820,
                lineHeight: 1.6,
              }}
            >
              El recuento corresponde al stock observado el día 01 a primera hora del mes
              seleccionado. Si el mes no tiene datos guardados, se mostrará como referencia el
              último recuento anterior disponible.
            </Typography>

            <Typography
              sx={{
                display: shouldUseTabletLayout ? 'block' : 'none',
                mt: 0.5,
                color: '#94a3b8',
                fontSize: '0.84rem',
                lineHeight: 1.45,
              }}
            >
              Corte del día 01. Completa las cantidades observadas en almacén.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: { xs: '100%', md: 'auto' },
              flexShrink: 0,
            }}
          >
            <TextField
              select
              label="Mes"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(Number(event.target.value))}
              size="small"
              sx={{
                ...selectorSx,
                flex: { xs: 1, md: 'initial' },
              }}
            >
              {MONTH_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Año"
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              size="small"
              sx={{
                ...selectorSx,
                minWidth: 120,
                flex: { xs: 1, md: 'initial' },
              }}
            >
              {YEAR_OPTIONS.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Box>

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: shouldUseTabletLayout ? 'visible' : 'hidden',
          border: `1px solid ${alpha('#94a3b8', 0.14)}`,
          bgcolor: 'rgba(8,18,36,0.9)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            alignItems="center"
            sx={{ display: shouldUseTabletLayout ? 'none' : 'flex' }}
          >
            {!canImport ? (
              <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                {IMPORT_PERMISSION_DENIED_TEXT}
              </Typography>
            ) : null}
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={openImportDialog}
              sx={secondaryButtonSx}
              disabled={isBulkSaving || !canImport}
            >
              Importar
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={exportCurrentMonth}
              sx={secondaryButtonSx}
              disabled={isBulkSaving || rows.length === 0}
            >
              Exportar
            </Button>

            <Button
              variant="outlined"
              startIcon={<SaveRoundedIcon />}
              onClick={() => void saveAllDirtyRows()}
              sx={secondaryButtonSx}
              disabled={dirtyRowsCount === 0 || isBulkSaving}
            >
              {isBulkSaving
                ? 'Guardando...'
                : dirtyRowsCount > 0
                  ? `Guardar cambios (${dirtyRowsCount})`
                  : 'Guardar cambios'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={addCatalogRow}
              sx={secondaryButtonSx}
              disabled={isBulkSaving}
            >
              Añadir desde catálogo
            </Button>

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={addExternalRow}
              sx={primaryButtonSx}
              disabled={isBulkSaving}
            >
              Añadir material
            </Button>
          </Stack>

          <Stack
            spacing={1}
            sx={{ display: shouldUseTabletLayout ? 'flex' : 'none' }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={addExternalRow}
                disabled={isBulkSaving}
                fullWidth
                sx={{
                  ...primaryButtonSx,
                  minHeight: 46,
                }}
              >
                Añadir material
              </Button>

              <Button
                variant="outlined"
                onClick={() =>
                  setMobileActionsOpen(
                    (current) => !current,
                  )
                }
                disabled={isBulkSaving}
                fullWidth
                sx={{
                  ...secondaryButtonSx,
                  minHeight: 46,
                }}
              >
                {mobileActionsOpen
                  ? 'Ocultar acciones'
                  : 'Más acciones'}
              </Button>
            </Stack>

            {mobileActionsOpen ? (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  bgcolor: 'rgba(2,6,23,0.24)',
                  border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddRoundedIcon />}
                  onClick={addCatalogRow}
                  sx={secondaryButtonSx}
                  disabled={isBulkSaving}
                >
                  Desde catálogo
                </Button>

                {!canImport ? (
                  <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                    {IMPORT_PERMISSION_DENIED_TEXT}
                  </Typography>
                ) : null}
                <Button
                  variant="outlined"
                  startIcon={<UploadFileRoundedIcon />}
                  onClick={openImportDialog}
                  sx={secondaryButtonSx}
                  disabled={isBulkSaving || !canImport}
                >
                  Importar
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={exportCurrentMonth}
                  sx={secondaryButtonSx}
                  disabled={isBulkSaving || rows.length === 0}
                >
                  Exportar
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Box>

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              display: shouldUseTabletLayout ? 'block' : 'none',
              p: 1.5,
              borderRadius: 2.75,
              bgcolor: 'rgba(2,6,23,0.28)',
              border: `1px solid ${alpha('#94a3b8', 0.12)}`,
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    color: '#cbd5e1',
                    fontSize: '0.8rem',
                    fontWeight: 900,
                  }}
                >
                  Progreso del recuento
                </Typography>

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontSize: '0.9rem',
                    fontWeight: 900,
                  }}
                >
                  {inventorySummary.completed} / {inventorySummary.total}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <LinearProgress
                  variant="determinate"
                  value={inventoryCompletionPercentage}
                  sx={{
                    flex: 1,
                    height: 7,
                    borderRadius: 999,
                    bgcolor: 'rgba(148,163,184,0.14)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: '#38bdf8',
                    },
                  }}
                />

                <Typography
                  sx={{
                    color: '#bae6fd',
                    fontSize: '0.78rem',
                    fontWeight: 900,
                    minWidth: 38,
                    textAlign: 'right',
                  }}
                >
                  {inventoryCompletionPercentage} %
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                flexWrap="wrap"
              >
                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                  }}
                >
                  {inventorySummary.pending} sin completar
                </Typography>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: 900,
                  }}
                >
                  ·
                </Typography>

                <Typography
                  sx={{
                    color: inventorySummary.dirty > 0 ? '#fde68a' : '#94a3b8',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                  }}
                >
                  {inventorySummary.dirty} sin guardar
                </Typography>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: 900,
                  }}
                >
                  ·
                </Typography>

                <Typography
                  sx={{
                    color: inventorySummary.external > 0 ? '#ddd6fe' : '#94a3b8',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                  }}
                >
                  {inventorySummary.external} externas
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            flexWrap="wrap"
            sx={{ display: shouldUseTabletLayout ? 'none' : 'flex' }}
          >
            <MetricCard label="Líneas totales" value={String(inventorySummary.total)} />
            <MetricCard label="Sin completar" value={String(inventorySummary.pending)} />
            <MetricCard label="Completadas" value={String(inventorySummary.completed)} />
            <MetricCard label="Cambios sin guardar" value={String(inventorySummary.dirty)} />
            <MetricCard label="Heredadas" value={String(inventorySummary.inheritedOnly)} />
            <MetricCard label="Externas" value={String(inventorySummary.external)} />
          </Stack>
        </Box>

        <Box
          sx={{
            position: shouldUseTabletLayout ? 'sticky' : 'static',
            top: shouldUseTabletLayout ? 8 : 'auto',
            zIndex: 20,
            px: { xs: 2, md: 3 },
            pt: shouldUseTabletLayout ? 1.5 : 0,
            pb: 2,
            bgcolor: shouldUseTabletLayout
              ? 'rgba(8,18,36,0.98)'
              : 'transparent',
            borderBottom: shouldUseTabletLayout
              ? `1px solid ${alpha('#94a3b8', 0.14)}`
              : 'none',
            boxShadow: shouldUseTabletLayout
              ? '0 14px 28px rgba(2,6,23,0.24)'
              : 'none',
            backdropFilter: shouldUseTabletLayout ? 'blur(10px)' : 'none',
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
              sx={{ display: shouldUseTabletLayout ? 'flex' : 'none' }}
            >
              <Typography
                sx={{
                  color: '#e2e8f0',
                  fontSize: '0.84rem',
                  fontWeight: 900,
                }}
              >
                Mostrando {filteredRows.length} de {inventorySummary.total}
              </Typography>

              <Typography
                sx={{
                  color: inventorySummary.dirty > 0 ? '#fde68a' : '#94a3b8',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                }}
              >
                {inventorySummary.dirty > 0
                  ? `${inventorySummary.dirty} sin guardar`
                  : 'Todo guardado'}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <InventorySearchField
                value={searchInputTerm}
                onChange={setSearchInputTerm}
              />

              <TextField
                select
                label="Proveedor"
                value={supplierFilter}
                onChange={(event) => setSupplierFilter(event.target.value)}
                size="small"
                sx={{
                  ...fieldSx,
                  minWidth: { xs: '100%', md: 240 },
                }}
              >
                <MenuItem value="all">Todos</MenuItem>
                {providerOptions.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
            >
              <Button
                variant={rowStatusFilter === 'all' ? 'contained' : 'outlined'}
                onClick={() => setRowStatusFilter('all')}
                sx={{
                  ...(rowStatusFilter === 'all' ? primaryButtonSx : secondaryButtonSx),
                  minHeight: shouldUseTabletLayout ? 42 : 'auto',
                }}
              >
                Todas ({inventorySummary.total})
              </Button>

              <Button
                variant={rowStatusFilter === 'pending' ? 'contained' : 'outlined'}
                onClick={() => setRowStatusFilter('pending')}
                sx={{
                  ...(rowStatusFilter === 'pending' ? primaryButtonSx : secondaryButtonSx),
                  minHeight: shouldUseTabletLayout ? 42 : 'auto',
                }}
              >
                Sin completar ({inventorySummary.pending})
              </Button>

              <Button
                variant={rowStatusFilter === 'completed' ? 'contained' : 'outlined'}
                onClick={() => setRowStatusFilter('completed')}
                sx={{
                  ...(rowStatusFilter === 'completed' ? primaryButtonSx : secondaryButtonSx),
                  minHeight: shouldUseTabletLayout ? 42 : 'auto',
                }}
              >
                Completadas ({inventorySummary.completed})
              </Button>

              <Button
                variant={rowStatusFilter === 'dirty' ? 'contained' : 'outlined'}
                onClick={() => setRowStatusFilter('dirty')}
                sx={{
                  ...(rowStatusFilter === 'dirty' ? primaryButtonSx : secondaryButtonSx),
                  minHeight: shouldUseTabletLayout ? 42 : 'auto',
                }}
              >
                Sin guardar ({inventorySummary.dirty})
              </Button>
            </Stack>

            <TextField
              select
              label="Orden"
              value={sortMode}
              onChange={(event) =>
                setSortMode(
                  event.target.value as InventorySortMode,
                )
              }
              size="small"
              sx={{
                ...fieldSx,
                minWidth: { xs: '100%', md: 240 },
                alignSelf: 'flex-start',
              }}
            >
              <MenuItem value="default">
                Orden actual
              </MenuItem>

              <MenuItem value="pending">
                Pendientes primero
              </MenuItem>

              <MenuItem value="alphabetical">
                Material A–Z
              </MenuItem>
            </TextField>
          </Stack>
        </Box>

        <Box
          sx={{
            display: shouldUseTabletLayout ? 'block' : 'none',
            px: 2,
            pb: 2,
          }}
        >
          {isLoading || isLoadingMaterials ? (
            <Stack alignItems="center" spacing={1.25} sx={{ py: 4 }}>
              <CircularProgress size={26} />
              <Typography sx={{ color: '#94a3b8' }}>
                Cargando recuentos...
              </Typography>
            </Stack>
          ) : filteredRows.length === 0 ? (
            <Typography sx={{ color: '#94a3b8', py: 2 }}>
              No hay líneas que coincidan con los filtros actuales.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {filteredRows.map((row) => (
                <InventoryTabletCard
                  key={row.local_id}
                  row={row}
                  locationLabels={(
                    locationAssignmentsByRowLocalId[
                      row.local_id
                    ] ?? []
                  )
                    .map(
                      (assignment) =>
                        assignment.location_operational_label,
                    )
                    .filter(
                      (
                        label,
                      ): label is string =>
                        Boolean(label),
                    )}
                  canAssignLocations={
                    activeWarehouseMaps.length > 0
                  }
                  onOpenLocations={
                    setLocationDialogRow
                  }
                  materialOptions={materialOptions}
                  manualMaterialOptions={
                    manualMaterialOptions
                  }
                  supplierOptions={
                    getSupplierSuggestionsForRow(row)
                  }
                  isBulkSaving={isBulkSaving}
                  onUpdateRow={updateRow}
                  onMaterialChange={handleMaterialChange}
                  onSave={(currentRow) => void saveRow(currentRow)}
                  onRemove={(currentRow) => void removeRow(currentRow)}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            display: shouldUseTabletLayout ? 'block' : 'none',
            px: 2,
            pb: 2,
          }}
        >
          <Stack
            spacing={1}
            sx={{
              pt: 1.5,
              borderTop: `1px solid ${alpha('#94a3b8', 0.14)}`,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={addExternalRow}
              disabled={isBulkSaving}
              sx={{
                ...secondaryButtonSx,
                alignSelf: 'flex-start',
                minHeight: 42,
              }}
            >
              Añadir material
            </Button>

            {dirtyRowsCount > 0 ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  bgcolor: 'rgba(2,6,23,0.28)',
                  border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                }}
              >
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontSize: '0.84rem',
                    fontWeight: 900,
                  }}
                >
                  {dirtyRowsCount === 1
                    ? '1 cambio sin guardar'
                    : `${dirtyRowsCount} cambios sin guardar`}
                </Typography>

                <Button
                  variant="contained"
                  startIcon={
                    isBulkSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveRoundedIcon />
                    )
                  }
                  onClick={() => void saveAllDirtyRows()}
                  disabled={isBulkSaving}
                  sx={{
                    ...primaryButtonSx,
                    minHeight: 42,
                    flexShrink: 0,
                  }}
                >
                  {isBulkSaving ? 'Guardando...' : 'Guardar todo'}
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Box>

        {!shouldUseTabletLayout ? (
          <Box
            sx={{
              overflowX: 'auto',
            }}
          >
          <Table sx={{ minWidth: INVENTORY_LOCATIONS_ENABLED ? 1540 : 1300 }}>
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-root': {
                    color: '#94a3b8',
                    bgcolor: 'rgba(15,23,42,0.78)',
                    borderBottom: `1px solid ${alpha('#94a3b8', 0.14)}`,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0.7,
                  },
                }}
              >
                <TableCell sx={{ minWidth: 320 }}>Material</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Proveedor</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Recuento anterior</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Recuento actual</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Unidad</TableCell>
                {INVENTORY_LOCATIONS_ENABLED ? (
                  <TableCell sx={{ minWidth: 240 }}>Ubicación habitual</TableCell>
                ) : null}
                <TableCell sx={{ minWidth: 220 }}>Notas</TableCell>
                <TableCell align="right" sx={{ width: 170 }}>
                  Acción
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading || isLoadingMaterials ? (
                <TableRow>
                  <TableCell colSpan={INVENTORY_LOCATIONS_ENABLED ? 8 : 7}>
                    <Typography sx={{ color: '#94a3b8', py: 1 }}>
                      Cargando recuentos...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={INVENTORY_LOCATIONS_ENABLED ? 8 : 7}>
                    <Typography sx={{ color: '#94a3b8', py: 1 }}>
                      No hay líneas que coincidan con los filtros actuales.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row) => (
                  <TableRow
                    key={row.local_id}
                    sx={getInventoryRowSx(row)}
                  >
                    <TableCell>
                      {row.is_external ? (
                        <Stack spacing={1}>
                          <Autocomplete
                            freeSolo
                            options={manualMaterialOptions}
                            value={row.external_material_name}
                            onChange={(_, nextValue) =>
                              updateRow(row.local_id, (current) => ({
                                ...current,
                                external_material_name:
                                  nextValue ?? '',
                              }))
                            }
                            onInputChange={(_, nextValue, reason) => {
                              if (reason !== 'input') {
                                return;
                              }

                              updateRow(row.local_id, (current) => ({
                                ...current,
                                external_material_name:
                                  nextValue,
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Material"
                                sx={fieldSx}
                              />
                            )}
                          />
                        </Stack>
                      ) : (
                        <Stack spacing={1}>
                          <TextField
                            select
                            value={row.material_id ?? ''}
                            onChange={(event) =>
                              handleMaterialChange(row.local_id, event.target.value)
                            }
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={fieldSx}
                          >
                            <MenuItem value="">Seleccionar material</MenuItem>
                            {materialOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={
                          getSupplierSuggestionsForRow(row)
                        }
                        value={row.supplier}
                        onChange={(_, nextValue) =>
                          updateRow(row.local_id, (current) => ({
                            ...current,
                            supplier: nextValue ?? '',
                          }))
                        }
                        onInputChange={(_, nextValue, reason) => {
                          if (reason !== 'input') {
                            return;
                          }

                          updateRow(row.local_id, (current) => ({
                            ...current,
                            supplier: nextValue,
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Proveedor"
                            sx={fieldSx}
                          />
                        )}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ color: '#cbd5e1', pt: 1 }}>
                        {row.previous_quantity || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <TextField
                        value={row.quantity}
                        onChange={(event) =>
                          updateRow(row.local_id, (current) => ({
                            ...current,
                            quantity: event.target.value,
                          }))
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="0"
                        sx={fieldSx}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ color: '#cbd5e1', pt: 1 }}>
                        {formatInventoryUnitLabel(row.material_unit_label)}
                      </Typography>
                    </TableCell>

                    {INVENTORY_LOCATIONS_ENABLED ? (
                      <TableCell sx={{ minWidth: 240 }}>
                        <Stack spacing={0.65}>
                          <Typography
                            sx={{
                              color:
                                (
                                  locationAssignmentsByRowLocalId[
                                    row.local_id
                                  ] ?? []
                                ).length > 0
                                  ? '#bae6fd'
                                  : '#64748b',
                              fontSize: '0.76rem',
                              fontWeight: 800,
                              lineHeight: 1.35,
                            }}
                          >
                            {(
                              locationAssignmentsByRowLocalId[
                                row.local_id
                              ] ?? []
                            ).length > 0
                              ? (
                                  locationAssignmentsByRowLocalId[
                                    row.local_id
                                  ] ?? []
                                )
                                  .map(
                                    (assignment) =>
                                      assignment.location_operational_label,
                                  )
                                  .filter(Boolean)
                                  .join(' · ')
                              : 'Sin ubicación habitual'}
                          </Typography>

                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PlaceRoundedIcon />}
                            onClick={() =>
                              setLocationDialogRow(row)
                            }
                            disabled={
                              activeWarehouseMaps.length === 0 ||
                              row.is_saving ||
                              isBulkSaving
                            }
                            sx={{
                              alignSelf: 'flex-start',
                              color: '#bae6fd',
                              borderColor:
                                'rgba(56,189,248,0.24)',
                              fontWeight: 900,
                              textTransform: 'none',
                            }}
                          >
                            Asignar
                          </Button>
                        </Stack>
                      </TableCell>
                    ) : null}

                    <TableCell>
                      <TextField
                        value={row.notes}
                        onChange={(event) =>
                          updateRow(row.local_id, (current) => ({
                            ...current,
                            notes: event.target.value,
                          }))
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Notas"
                        sx={fieldSx}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          onClick={() => copyPreviousQuantity(row.local_id)}
                          disabled={row.is_saving || isBulkSaving}
                          sx={{
                            color: '#cbd5e1',
                            '&:hover': {
                              color: '#f8fafc',
                              bgcolor: 'rgba(148,163,184,0.08)',
                            },
                          }}
                        >
                          <ContentCopyRoundedIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          onClick={() => void saveRow(row)}
                          disabled={row.is_saving || isBulkSaving}
                          sx={{
                            color: '#7dd3fc',
                            '&:hover': {
                              color: '#bae6fd',
                              bgcolor: 'rgba(14,165,233,0.10)',
                            },
                          }}
                        >
                          <SaveRoundedIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          onClick={() => void removeRow(row)}
                          disabled={row.is_saving || isBulkSaving}
                          sx={{
                            color: '#94a3b8',
                            '&:hover': {
                              color: '#fda4af',
                              bgcolor: 'rgba(244,63,94,0.08)',
                            },
                          }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </Box>
        ) : null}
      </Paper>

      {INVENTORY_LOCATIONS_ENABLED ? (
        <InventoryLocationPickerDialog
          open={Boolean(locationDialogRow)}
          row={locationDialogRow}
          activeMaps={activeWarehouseMaps}
          rowAssignments={
            locationDialogRow
              ? locationAssignmentsByRowLocalId[
                  locationDialogRow.local_id
                ] ?? []
              : []
          }
          onClose={() =>
            setLocationDialogRow(null)
          }
          onSaved={
            reloadWarehouseLocationMetadata
          }
        />
      ) : null}

      <Dialog
        open={importDialogOpen}
        onClose={isPreviewLoading || isConfirmLoading ? undefined : closeImportDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            color: '#e2e8f0',
            border: '1px solid rgba(148,163,184,0.16)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'rgba(15,23,42,0.98)',
            color: '#f8fafc',
            borderBottom: '1px solid rgba(148,163,184,0.12)',
            fontWeight: 800,
          }}
        >
          Importar recuento de inventario
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: 'rgba(15,23,42,0.98)',
            borderColor: 'rgba(148,163,184,0.12)',
          }}
        >
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Alert
              severity="info"
              sx={{
                bgcolor: 'rgba(8,47,73,0.45)',
                color: '#bae6fd',
                border: '1px solid rgba(14,165,233,0.18)',
                '& .MuiAlert-icon': {
                  color: '#38bdf8',
                },
              }}
            >
              La importación se aplicará al recuento de <strong>{selectedMonthLabel} {selectedYear}</strong>,
              correspondiente al corte del día 01.
            </Alert>

            <Stack direction="row" spacing={1} alignItems="center">
              <StepPill active={importStep === 1} completed={importStep > 1} label="1. Mapeo" />
              <StepPill active={importStep === 2} completed={false} label="2. Resolución" />
            </Stack>

            {importError ? (
              <Alert
                severity="error"
                sx={{
                  bgcolor: 'rgba(127,29,29,0.35)',
                  color: '#fecaca',
                  border: '1px solid rgba(248,113,113,0.18)',
                  '& .MuiAlert-icon': {
                    color: '#f87171',
                  },
                }}
              >
                {importError}
              </Alert>
            ) : null}

            {importConfirmSummary ? (
              <Alert
                severity="success"
                sx={{
                  bgcolor: 'rgba(20,83,45,0.32)',
                  color: '#bbf7d0',
                  border: '1px solid rgba(74,222,128,0.18)',
                  '& .MuiAlert-icon': {
                    color: '#4ade80',
                  },
                }}
              >
                Importación completada. Creadas: {importConfirmSummary.rows_created}. Actualizadas:{' '}
                {importConfirmSummary.rows_updated}. Importadas: {importConfirmSummary.rows_imported}.
              </Alert>
            ) : null}
            {isConfirmLoading ? (
              <Alert
                severity="info"
                sx={{
                  bgcolor: 'rgba(8,47,73,0.45)',
                  color: '#bae6fd',
                  border: '1px solid rgba(14,165,233,0.18)',
                  '& .MuiAlert-icon': {
                    color: '#38bdf8',
                  },
                }}
              >
                Importando datos y actualizando el recuento del mes seleccionado...
              </Alert>
            ) : null}

            {importStep === 1 ? (
              <Stack spacing={3}>
                <Box>
                  <Typography sx={dialogSectionTitleSx}>Archivo</Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileRoundedIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isPreviewLoading || isConfirmLoading}
                      sx={dialogSecondaryButtonSx}
                    >
                      Seleccionar archivo
                    </Button>

                    <Typography sx={{ color: '#94a3b8', alignSelf: 'center' }}>
                      {importFile ? importFile.name : 'Ningún archivo seleccionado'}
                    </Typography>
                  </Stack>

                  {isPreviewLoading ? (
                    <Typography sx={{ color: '#94a3b8', mt: 1.25 }}>
                      Generando previsualización…
                    </Typography>
                  ) : null}

                  {importNormalization ? (
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      sx={{ mt: 2 }}
                    >
                      <TextField
                        select
                        label="Hoja"
                        value={importSheetName}
                        onChange={(event) => {
                          setImportSheetName(event.target.value);
                          setImportPreview(null);
                          setImportError(null);
                        }}
                        size="small"
                        fullWidth
                        sx={dialogFieldSx}
                      >
                        {importNormalization.sheets.map((sheet) => (
                          <MenuItem
                            key={sheet.name}
                            value={sheet.name}
                          >
                            {sheet.name}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        label="Fila de cabecera (o selecciona abajo)"
                        type="number"
                        value={importHeaderRowInput}
                        onChange={(event) => {
                          setImportHeaderRowInput(event.target.value);
                          setImportPreview(null);
                          setImportError(null);
                        }}
                        inputProps={{ min: 1 }}
                        size="small"
                        fullWidth
                        sx={dialogFieldSx}
                      />

                      <Button
                        variant="outlined"
                        onClick={() => {
                          void handleApplyImportSheet();
                        }}
                        disabled={isPreviewLoading}
                        sx={dialogSecondaryButtonSx}
                      >
                        Aplicar hoja y cabecera
                      </Button>
                    </Stack>
                  ) : null}

                  {/* IC-STOCK-01B5: cabecera seleccionable en la previsualizacion cruda. */}
                  {importNormalization && importSheetName !== importNormalization.selected_sheet ? (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      Has cambiado de hoja. Pulsa «Aplicar hoja y cabecera» para cargar
                      su contenido antes de elegir visualmente la fila de encabezados.
                    </Alert>
                  ) : importNormalization && importNormalization.raw_preview_rows.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={dialogSectionTitleSx}>
                        Vista previa del Excel · Selecciona la fila de encabezados
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, mb: 1 }}>
                        Pulsa una fila para marcarla como cabecera y, cuando este correcta,
                        pulsa «Aplicar hoja y cabecera». La seleccion no vuelve a cargar el archivo.
                      </Typography>
                      <Box sx={{ maxHeight: 290, overflow: 'auto', border: '1px solid #334155', borderRadius: 2 }}>
                        <Table size="small" stickyHeader>
                          <TableBody>
                            {importNormalization.raw_preview_rows.slice(0, 30).map((row, rowIndex) => {
                              const isHeader = Number(importHeaderRowInput) === rowIndex + 1;
                              const chooseHeader = () => {
                                if (isPreviewLoading) return;
                                setImportHeaderRowInput(String(rowIndex + 1));
                                setImportPreview(null);
                                setImportError(null);
                              };
                              return (
                                <TableRow
                                  key={rowIndex}
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Seleccionar fila ${rowIndex + 1} como cabecera`}
                                  onClick={chooseHeader}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      chooseHeader();
                                    }
                                  }}
                                  sx={{
                                    cursor: isPreviewLoading ? 'wait' : 'pointer',
                                    bgcolor: isHeader ? 'rgba(56,189,248,0.18)' : 'transparent',
                                    '&:hover': { bgcolor: 'rgba(56,189,248,0.12)' },
                                  }}
                                >
                                  <TableCell sx={{ color: isHeader ? '#38bdf8' : '#94a3b8', fontWeight: 800, whiteSpace: 'nowrap', borderColor: '#334155' }}>
                                    {isHeader ? `CABECERA · ${rowIndex + 1}` : `Fila ${rowIndex + 1}`}
                                  </TableCell>
                                  {row.map((cell, cellIndex) => (
                                    <TableCell key={cellIndex} sx={{ color: isHeader ? '#e2e8f0' : '#94a3b8', fontWeight: isHeader ? 700 : 400, whiteSpace: 'nowrap', borderColor: '#334155' }}>
                                      {String(cell ?? '').slice(0, 65)}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 1 }}>
                        La tabla muestra las primeras filas. El importador tambien procesa
                        los bloques posteriores de materias primas y embalajes de la misma hoja.
                      </Typography>
                    </Box>
                  ) : null}
                </Box>

                {importPreview ? (
                  <>
                    <Box>
                      <Typography sx={dialogSectionTitleSx}>Mapeo de columnas</Typography>

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
                        <TextField
                          select
                          label="Material *"
                          value={importMapping.material ?? ''}
                          onChange={(event) =>
                            setImportMapping((current) => ({
                              ...current,
                              material: event.target.value || null,
                            }))
                          }
                          size="small"
                          fullWidth
                          sx={dialogFieldSx}
                        >
                          <MenuItem value="">Sin asignar</MenuItem>
                          {importPreview.columns.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          label="Proveedor"
                          value={importMapping.supplier ?? ''}
                          onChange={(event) =>
                            setImportMapping((current) => ({
                              ...current,
                              supplier: event.target.value || null,
                            }))
                          }
                          size="small"
                          fullWidth
                          sx={dialogFieldSx}
                        >
                          <MenuItem value="">Sin asignar</MenuItem>
                          {importPreview.columns.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          label="Cantidad *"
                          value={importMapping.quantity ?? ''}
                          onChange={(event) =>
                            setImportMapping((current) => ({
                              ...current,
                              quantity: event.target.value || null,
                            }))
                          }
                          size="small"
                          fullWidth
                          sx={dialogFieldSx}
                        >
                          <MenuItem value="">Sin asignar</MenuItem>
                          {importPreview.columns.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          label="Notas"
                          value={importMapping.notes ?? ''}
                          onChange={(event) =>
                            setImportMapping((current) => ({
                              ...current,
                              notes: event.target.value || null,
                            }))
                          }
                          size="small"
                          fullWidth
                          sx={dialogFieldSx}
                        >
                          <MenuItem value="">Sin asignar</MenuItem>
                          {importPreview.columns.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>

                      {importPreview.missing_required_fields.length > 0 ? (
                        <Alert
                          severity="warning"
                          sx={{
                            mt: 1.5,
                            bgcolor: 'rgba(120,53,15,0.35)',
                            color: '#fde68a',
                            border: '1px solid rgba(251,191,36,0.18)',
                            '& .MuiAlert-icon': {
                              color: '#fbbf24',
                            },
                          }}
                        >
                          Faltan campos obligatorios en el mapeo sugerido:{' '}
                          {importPreview.missing_required_fields.join(', ')}
                        </Alert>
                      ) : null}
                    </Box>

                    <Box>
                      <Typography sx={dialogSectionTitleSx}>Resumen</Typography>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
                        <MetricCard label="Filas" value={String(importPreview.coverage_summary.total_rows)} />
                        <MetricCard
                          label="Con material"
                          value={String(importPreview.coverage_summary.rows_with_material_ref)}
                        />
                        <MetricCard
                          label="Con cantidad válida"
                          value={String(importPreview.coverage_summary.rows_with_valid_quantity)}
                        />
                        <MetricCard
                          label="Materiales detectados"
                          value={String(importPreview.detected_material_refs.length)}
                        />
                      </Stack>
                    </Box>

                    <Box>
                      <Typography sx={dialogSectionTitleSx}>Vista previa</Typography>
                      <Box
                        sx={{
                          overflowX: 'auto',
                          mt: 1.5,
                          border: '1px solid rgba(148,163,184,0.14)',
                          borderRadius: 2,
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                '& .MuiTableCell-root': {
                                  color: '#94a3b8',
                                  bgcolor: 'rgba(30,41,59,0.82)',
                                  borderBottom: '1px solid rgba(148,163,184,0.12)',
                                  fontWeight: 800,
                                },
                              }}
                            >
                              <TableCell>Material</TableCell>
                              <TableCell>Proveedor</TableCell>
                              <TableCell>Cantidad</TableCell>
                              <TableCell>Notas</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {importPreview.preview_rows.map((row, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  '& .MuiTableCell-root': {
                                    color: '#e2e8f0',
                                    borderBottom: '1px solid rgba(148,163,184,0.08)',
                                    bgcolor: 'rgba(15,23,42,0.92)',
                                  },
                                }}
                              >
                                <TableCell>{String(row.material ?? '—')}</TableCell>
                                <TableCell>{String(row.supplier ?? '—')}</TableCell>
                                <TableCell>{String(row.quantity ?? '—')}</TableCell>
                                <TableCell>{String(row.notes ?? '—')}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  </>
                ) : null}
              </Stack>
            ) : null}

            {importStep === 2 && importPreview ? (
              <Stack spacing={3}>
                <Box>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                  >
                    <Typography sx={dialogSectionTitleSx}>Resolución de materiales</Typography>

                    <Button
                      variant="outlined"
                      onClick={markAllUnresolvedAsExternal}
                      sx={dialogSecondaryButtonSx}
                    >
                      Marcar no resueltos como externos
                    </Button>
                  </Stack>

                  <Typography sx={{ color: '#94a3b8', mt: 1 }}>
                    Las referencias que ya existen se vinculan automáticamente.
                    Las referencias nuevas se crearán automáticamente al confirmar.
                    Solo utiliza una vinculación manual si una referencia debe
                    asociarse realmente a otro material ya existente.
                  </Typography>
                </Box>

                {importPreview.detected_material_refs.length === 0 ? (
                  <Typography sx={{ color: '#94a3b8' }}>
                    No se detectaron materiales en el archivo.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {importPreview.detected_material_refs.map((item) => {
                      const manualChoice = materialResolution[item.normalized_value];

                      return (
                        <Paper
                          key={item.normalized_value}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: 'rgba(15,23,42,0.92)',
                            borderColor: 'rgba(148,163,184,0.16)',
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={1.5}
                            justifyContent="space-between"
                          >
                            <Box sx={{ minWidth: 240 }}>
                              <Typography sx={{ fontWeight: 700, color: '#f8fafc' }}>
                                {item.source_value}
                              </Typography>
                              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                                Filas afectadas: {item.row_count}
                              </Typography>
                            </Box>

                            {item.is_resolved ? (
                              <Alert
                                severity="success"
                                sx={{
                                  flex: 1,
                                  bgcolor: 'rgba(20,83,45,0.28)',
                                  color: '#bbf7d0',
                                  border: '1px solid rgba(74,222,128,0.18)',
                                  '& .MuiAlert-icon': {
                                    color: '#4ade80',
                                  },
                                }}
                              >
                                Resuelto automáticamente como{' '}
                                <strong>
                                  {item.resolved_material_code
                                    ? `${item.resolved_material_code} · ${item.resolved_material_name ?? ''}`
                                    : item.resolved_material_name ?? 'material existente'}
                                </strong>
                                .
                              </Alert>
                            ) : (
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1.5}
                                sx={{ flex: 1 }}
                              >
                                <TextField
                                  select
                                  label="Resolver como"
                                  value={
                                    manualChoice?.mode === 'external'
                                      ? '__external__'
                                      : manualChoice?.material_id ?? ''
                                  }
                                  onChange={(event) =>
                                    handleChangeResolutionMode(item, event.target.value)
                                  }
                                  size="small"
                                  fullWidth
                                  sx={dialogFieldSx}
                                >
                                  <MenuItem value="">Crear automáticamente</MenuItem>
                                  <MenuItem value="__external__">Registrar como externo</MenuItem>
                                  {materialOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'rgba(15,23,42,0.98)',
            borderTop: '1px solid rgba(148,163,184,0.12)',
          }}
        >
          <Button
            onClick={closeImportDialog}
            disabled={isPreviewLoading || isConfirmLoading}
            sx={{ color: '#94a3b8' }}
          >
            Cerrar
          </Button>

          {importStep === 2 ? (
            <Button
              variant="outlined"
              onClick={() => setImportStep(1)}
              disabled={isPreviewLoading || isConfirmLoading}
              sx={dialogSecondaryButtonSx}
            >
              Volver al mapeo
            </Button>
          ) : null}

          {importStep === 1 ? (
            <Button
              variant="contained"
              onClick={() => {
                void goToImportStepTwo();
              }}
              disabled={!importPreview || isPreviewLoading || isConfirmLoading}
              sx={dialogPrimaryButtonSx}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => void handleConfirmImport()}
              disabled={!importPreview || isPreviewLoading || isConfirmLoading}
              sx={dialogPrimaryButtonSx}
              startIcon={
                isConfirmLoading ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: '#082f49',
                    }}
                  />
                ) : undefined
              }
            >
              {isConfirmLoading ? 'Importando...' : 'Confirmar importación'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function StepPill({
  label,
  active,
  completed,
}: {
  label: string;
  active: boolean;
  completed: boolean;
}) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        border: '1px solid',
        borderColor: active
          ? 'rgba(56,189,248,0.35)'
          : completed
            ? 'rgba(74,222,128,0.28)'
            : 'rgba(148,163,184,0.18)',
        bgcolor: active
          ? 'rgba(8,47,73,0.42)'
          : completed
            ? 'rgba(20,83,45,0.24)'
            : 'rgba(30,41,59,0.7)',
      }}
    >
      <Typography
        sx={{
          color: active ? '#bae6fd' : completed ? '#bbf7d0' : '#cbd5e1',
          fontSize: '0.84rem',
          fontWeight: 800,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        minWidth: 140,
        bgcolor: 'rgba(15,23,42,0.92)',
        borderColor: 'rgba(148,163,184,0.16)',
      }}
    >
      <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '1.15rem', mt: 0.35 }}>
        {value}
      </Typography>
    </Paper>
  );
}

const dialogSectionTitleSx = {
  color: '#f8fafc',
  fontWeight: 800,
  fontSize: '1rem',
};

const dialogFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f8fafc',
    bgcolor: 'rgba(30,41,59,0.88)',
    '& fieldset': {
      borderColor: 'rgba(148,163,184,0.18)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(148,163,184,0.32)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(56,189,248,0.55)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#94a3b8',
  },
  '& .MuiSvgIcon-root': {
    color: '#cbd5e1',
  },
};

const dialogPrimaryButtonSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 800,
  textTransform: 'none',
  bgcolor: '#06b6d4',
  color: '#082f49',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: '#22d3ee',
    boxShadow: 'none',
  },
};

const dialogSecondaryButtonSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 800,
  textTransform: 'none',
  color: '#cbd5e1',
  borderColor: 'rgba(148,163,184,0.22)',
  '&:hover': {
    borderColor: 'rgba(148,163,184,0.38)',
    bgcolor: 'rgba(148,163,184,0.06)',
  },
};

const selectorSx = {
  minWidth: 160,
  '& .MuiOutlinedInput-root': {
    color: '#f8fafc',
    bgcolor: 'rgba(15,23,42,0.72)',
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(148,163,184,0.20)',
  },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f8fafc',
    bgcolor: 'rgba(15,23,42,0.66)',
    borderRadius: 2.5,
    '& fieldset': {
      borderColor: 'rgba(148,163,184,0.16)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(148,163,184,0.28)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(56,189,248,0.55)',
    },
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#64748b',
    opacity: 1,
  },
};

const primaryButtonSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 800,
  textTransform: 'none',
  bgcolor: '#0ea5e9',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: '#38bdf8',
    boxShadow: 'none',
  },
};

const secondaryButtonSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 800,
  textTransform: 'none',
  color: '#e2e8f0',
  borderColor: 'rgba(148,163,184,0.24)',
  '&:hover': {
    borderColor: 'rgba(148,163,184,0.4)',
    bgcolor: 'rgba(148,163,184,0.06)',
  },
};